import { Navigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardDescription, CardTitle } from '@/components/ui/card'
import { ProviderIcon } from '@/components/provider-icon'
import { useSession } from '@/features/auth/use-session'
import { AUTH_BASE_URL } from '@/lib/api'

/**
 * Staff sign-in is an OAuth redirect handled entirely by cwc-backend-v1 — the
 * SPA never sees a credential or a token, it just gets a session cookie back.
 * `intent=login` is required and explicit: inferring login-vs-link from cookie
 * presence caused an account-crossover bug (backend api-contract.md §"Login vs
 * link").
 *
 * Both providers, same as the consumer frontend's ProviderButtons.vue —
 * staff accounts are ordinary consumer accounts (see arch doc §8 Q3, now
 * resolved: same Google/Apple as consumers, no staff-specific IdP), so
 * whichever provider they originally signed up with must work here too.
 *
 * `origin=admin` tells cwc-backend-v1's OAuth callback to land back on this
 * app's own /confirm route (confirm-page.tsx) instead of the consumer
 * frontend's — see AuthOptions.AdminBaseUrl in cwc-backend-v1.
 *
 * Built off AUTH_BASE_URL, NOT this console's own admin API base — /auth/*
 * is served by cwc-backend-v1 on its own domain (e.g. api-test.coinworldcap.com),
 * a different deployment from this console's own /api/v1/admin/* backend.
 */
const PROVIDERS = ['google', 'apple'] as const

function loginUrl(provider: (typeof PROVIDERS)[number]) {
  return `${AUTH_BASE_URL}/auth/oauth/${provider}?intent=login&redirect=/users&origin=admin`
}

export function LoginPage() {
  const { user, isLoading } = useSession()

  if (isLoading) return null
  if (user) return <Navigate to="/users" replace />

  return (
    <div className="grid h-full place-items-center p-6">
      <Card className="w-full max-w-sm text-center">
        <CardTitle>CoinWorldCap Operations Console</CardTitle>
        <CardDescription className="mt-2">
          Staff access only. Sign in with your CoinWorldCap account.
        </CardDescription>
        <div className="mt-5 flex flex-col gap-2.5">
          {PROVIDERS.map((provider) => (
            <Button
              key={provider}
              variant="secondary"
              className="w-full"
              onClick={() => window.location.assign(loginUrl(provider))}
            >
              <ProviderIcon provider={provider} />
              Continue with {provider === 'google' ? 'Google' : 'Apple'}
            </Button>
          ))}
        </div>
      </Card>
    </div>
  )
}
